import React from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

const SHADE_HEX_MAP = {
  '1N':'#1A1A1A','1A':'#131313','1B':'#0F0F15',
  '2N':'#2C1810','2A':'#261813',
  '3N':'#3B2414','3A':'#362418','3G':'#3F2710','3R':'#3D1C10','3M':'#3A1915',
  '4N':'#4A2F1A','4NN':'#4D3220','4A':'#453020','4G':'#523518','4C':'#4F2816','4R':'#4C2215','4RR':'#4A1E12','4RB':'#4B2418','4M':'#482320','4V':'#432628',
  '5N':'#5D3C26','5NN':'#614028','5A':'#583D2A','5G':'#674520','5C':'#6A3820','5R':'#65301E','5RR':'#622818','5RB':'#633528','5RV':'#5E2F30','5M':'#5C3232','5V':'#573338','5B':'#5A3828',
  '6N':'#7A5436','6NN':'#7E5838','6A':'#75553C','6G':'#856030','6C':'#8A4E28','6R':'#844428','6RR':'#803820','6RB':'#7D4A35','6M':'#784540','6V':'#734248','6W':'#885A35',
  '7N':'#996D48','7NN':'#9D7050','7A':'#947050','7G':'#A67940','7C':'#AC6338','7R':'#A55838','7RR':'#A04830','7M':'#985850','7V':'#935A58','7W':'#A87548','7WG':'#B07A45',
  '8N':'#B8885A','8NN':'#BD8D60','8A':'#B38B65','8G':'#C59450','8C':'#CC7F48','8R':'#C57548','8V':'#B37568','8P':'#BF9075','8W':'#C89060',
  '9N':'#D9A872','9A':'#D4AB7E','9G':'#E5B368','9P':'#E0B488','9V':'#D49888',
  '10N':'#F5D0A0','10A':'#F0D3AA','10G':'#FFD88C','10P':'#FAD8B0','10V':'#F0C0B0',
};

const getHex = (code) => SHADE_HEX_MAP[code?.toUpperCase()] || '#5D3C26';

const MultiColorComparison = ({ originalImage, comparisons, loading, personMask }) => {
  if (loading) {
    return (
      <div className="w-full py-12 flex flex-col items-center justify-center space-y-4" data-testid="comparison-loading">
        <Loader2 className="h-16 w-16 text-accent-primary animate-spin" />
        <p className="font-sans text-text-secondary">Comparing colors...</p>
      </div>
    );
  }

  if (!comparisons || comparisons.length === 0) {
    return (
      <div className="w-full py-12 text-center" data-testid="comparison-empty">
        <p className="font-sans text-text-muted">Select colors to compare</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6" data-testid="multi-color-comparison">
      <div className="text-center mb-6">
        <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-2">
          Color Comparison
        </h2>
        <p className="font-sans text-text-secondary">See how each shade transforms your look</p>
      </div>

      <div className={`grid grid-cols-1 gap-6 ${comparisons.length === 1 ? 'md:grid-cols-1 max-w-2xl mx-auto' : comparisons.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
        {comparisons.map((comparison, index) => {
          const hex = comparison.hex || getHex(comparison.color_code);
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="space-y-3"
              data-testid={`comparison-${index}`}
            >
              {/* Image with color applied */}
              <div className="relative overflow-hidden rounded-sm border border-border-color">
                <img src={comparison.modified_image || originalImage} alt={`Preview ${comparison.color_code}`} className="w-full h-auto block" />
                
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/70 text-white px-3 py-2 rounded-sm">
                  <div className="w-5 h-5 rounded-full border border-white/40" style={{ backgroundColor: hex }} />
                  <span className="font-sans text-xs uppercase tracking-wider">{comparison.color_code}</span>
                </div>
              </div>

              {/* Description card */}
              <div className="bg-bg-secondary border border-border-color p-4 rounded-sm">
                <p className="font-serif text-lg text-text-primary font-medium mb-2">
                  {comparison.color_name}
                </p>
                <p className="font-sans text-sm text-text-secondary leading-relaxed line-clamp-4">
                  {comparison.description}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default MultiColorComparison;
